import { useCallback, useState } from 'react'
import req from '../api/request'
import { GameSettings } from '../types/GameSettings'

/**
 * Interface for game settings data
 */

/**
 * Hook result interface for useGameSettings
 */
interface UseGameSettingsResult {
  /**
   * The game settings data
   */
  settings: GameSettings | null
  /**
   * Loading state for the request
   */
  loading: boolean
  /**
   * Error state if request fails
   */
  error: Error | null
  /**
   * Function to manually fetch game settings
   */
  fetchSettings: (gameId: string) => Promise<GameSettings | null>
}

/**
 * Hook for fetching and managing game settings
 * @returns Object containing game settings data, loading state, error state, and fetch function
 */
export const useGameSettings = (): UseGameSettingsResult => {
  const [settings, setSettings] = useState<GameSettings | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Fetches game settings for a given game ID
   * @param gameId - The ID of the game to fetch settings for
   */
  const fetchSettings = useCallback(async (gameId: string) => {
    if (!gameId) {
      return null
    }
    setLoading(true)
    try {
      const data = await req(`/games/${gameId}/settings`)
      setSettings(data)
      setError(null)
      return data
    } catch (err: any) {
      setError(err)
      setSettings(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  return { settings, loading, error, fetchSettings }
}
